import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { DateAdapter } from '@angular/material/core';
import { Store } from '@ngrx/store';

import { KpiCrudService } from '../../../core/services/kpi-crud.service';
import { LoggerService } from '../../../core/services/logger.service';
import { UtilsService } from '../../../core/services/utils.service';
import { DataService } from '../../../core/services/data.service';
import { CatalogService } from '../../../core/services/catalog.service';
import { CustomerNameService } from '../../../core/services/customer-name.service';
import { FilterRefService } from '../../../core/services/filter-ref.service';
import {
  CommonAttribute,
  CommonAttributeRequest,
  KpiOperator
} from '../../../core/models/kpi.model';
import { MultiLangField, setMultiLanguageField } from '../../customization/multilang-input/multi-language';
import { AppStateInterface } from '../../../appState.interface';
import { selectedCustomerSelector, getCustomersSelector } from '../../../core/store/selectors';
import { SelectedCustomerCacheInterface } from '../../../core/store/interface/selectedCustomerCache.interface';
import { listAssets } from '../../../core/store/interface/selectedAssets.interface';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';

import {
  faPlus,
  faEdit,
  faTrash,
  faCheck,
  faTimes,
  faChevronDown,
  faChevronUp,
  faChevronLeft,
  faChevronRight,
  faCheckCircle,
  faSpinner,
  faInfoCircle,
  faSave,
  faSearch,
  faSort,
  faSortUp,
  faSortDown,
  faBan
} from '@fortawesome/free-solid-svg-icons';

// Scopes di default per i segnali (verranno aggiornati dinamicamente)
const DEFAULT_KPI_SCOPES = ['kpi', 'composite_signal', 'tcu', 'erp', 'platform'];

@Component({
  selector: 'app-kpi-configuration',
  templateUrl: './kpi-configuration.component.html',
  styleUrls: ['./kpi-configuration.component.scss']
})
export class KpiConfigurationComponent implements OnInit, OnChanges, OnDestroy {
  @Input() selectedAudience: 'USER' | 'CUSTOMER' | 'GLOBAL' = 'USER';
  @Input() customerId: string | null = null;
  @Input() set signalsInput(value: CommonAttribute[]) {
    if (value !== undefined && value !== null) {
      this.signals = value;
      this.logger.debug('kpi-configuration', `Ricevuti ${this.signals.length} segnali dal parent component`);

      // Verifica se il segnale attualmente selezionato è ancora nella lista
      if (this.selectedSignal && !this.signals.find(s => s.name === this.selectedSignal?.name)) {
        this.logger.info('kpi-configuration', `Segnale selezionato "${this.selectedSignal.name}" non più disponibile, reset selezione`);
        this.selectedSignal = null;
        this.clearTestResult();
      }
    }
  }
  @Output() configurationSaved = new EventEmitter<void>();

  private destroy$ = new Subject<void>();

  // FontAwesome icons
  faPlus = faPlus;
  faEdit = faEdit;
  faTrash = faTrash;
  faCheck = faCheck;
  faTimes = faTimes;
  faChevronDown = faChevronDown;
  faChevronUp = faChevronUp;
  faChevronLeft = faChevronLeft;
  faChevronRight = faChevronRight;
  faCheckCircle = faCheckCircle;
  faSpinner = faSpinner;
  faInfoCircle = faInfoCircle;
  faSave = faSave;
  faSearch = faSearch;
  faSort = faSort;
  faSortUp = faSortUp;
  faSortDown = faSortDown;
  faBan = faBan;

  // Costanti
  readonly MAX_VEHICLES = 25;

  // Stati UI
  isFormExpanded = false;
  isLoading = false;
  isSaving = false;
  isTesting = false;
  isEditing = false;
  editingKpiUid: string | null = null;

  // Dati
  existingKpis: CommonAttribute[] = [];
  filteredKpis: CommonAttribute[] = [];
  kpisByScope: { [scope: string]: CommonAttribute[] } = {};
  kpiScopes: string[] = [];
  signalsCatalog: CommonAttribute[] = [];
  signals: CommonAttribute[] = []; // Lista semplificata segnali con scope="signal"
  private fullCatalogData: any[] = []; // Snapshot completo del catalogo per il salvataggio
  signalsByScope: { [scope: string]: CommonAttribute[] } = {};
  kpiOperators: KpiOperator[] = [];
  currentCustomerUid: string | null = null;
  customerNameMap: Map<string, string> = new Map();
  availableVehicles: listAssets[] = [];

  // Ricerca
  searchTerm: string = '';

  // Paginazione
  currentPage: number = 1;
  pageSize: number = 10;

  // Ordinamento
  sortField: 'label' | 'name' | 'function' = 'label';
  sortDirection: 'asc' | 'desc' = 'asc';

  /**
   * Ritorna i segnali ordinati alfabeticamente per label o name
   */
  getSignalDisplayLabel(signal: CommonAttribute): string {
    if (signal.i18nLabel) {
      const lang = localStorage.getItem('language') || this.translateService.currentLang || 'en';
      const key = `${lang}-${lang.toUpperCase()}`;
      if (signal.i18nLabel[key]) return signal.i18nLabel[key];
    }
    return signal.label || signal.name || '';
  }

  get sortedSignals(): CommonAttribute[] {
    return [...this.signals].sort((a, b) =>
      this.getSignalDisplayLabel(a).toLowerCase().localeCompare(this.getSignalDisplayLabel(b).toLowerCase())
    );
  }

  // Inline editing
  inlineEditingKpiUid: string | null = null;
  inlineEditOperator: KpiOperator | null = null;
  inlineEditNameField: MultiLangField | null = null;
  inlineEditSaving: boolean = false;

  // Form fields
  selectedSignal: CommonAttribute | null = null;
  selectedOperator: KpiOperator | null = null;
  kpiNameField: MultiLangField;

  // Errori
  formError: string | null = null;
  testResult: { success: boolean; message: string; data?: any[]; isAggregated?: boolean; values?: Array<{vin: string; value: any}> } | null = null;
  isTestResultExpanded = true;

  // Test KPI form
  showTestForm = false;
  testStartDate: Date | null = null;
  testEndDate: Date | null = null;
  testSelectedVehicles: string[] = [];
  testSeparateData: boolean = false;
  vehicleFilterText: string = '';
  filteredAvailableVehicles: listAssets[] = [];
  stylesheetString: string = 'aft';

  constructor(
    private kpiCrudService: KpiCrudService,
    private translateService: TranslateService,
    private logger: LoggerService,
    private dialog: MatDialog,
    private dateAdapter: DateAdapter<any>,
    private store: Store<AppStateInterface>,
    public utilsService: UtilsService,
    private dataService: DataService,
    private catalogService: CatalogService,
    private customerNames: CustomerNameService,
    private filterRefService: FilterRefService
  ) {
    this.kpiNameField = new MultiLangField('name');
    setMultiLanguageField(this.kpiNameField);
  }

  ngOnInit(): void {
    this.stylesheetString = this.utilsService.getStylesheetString();
    this.setDatepickerLocale();
    if (!this.customerId) {
      this.subscribeToCustomer();
    }
    this.loadKpiOperators();
    this.store.select(getCustomersSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((customers: any[]) => {
        this.customerNameMap = new Map(customers.map(c => [c.uid, c.name]));
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customerId'] && changes['customerId'].currentValue) {
      this.currentCustomerUid = changes['customerId'].currentValue;
      this.loadExistingKpis();
      this.loadVehiclesForCustomer(this.currentCustomerUid!);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Sottoscrizione al customer selezionato
   */
  private subscribeToCustomer(): void {
    this.store.select(selectedCustomerSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((customer: SelectedCustomerCacheInterface) => {
        if (customer?.uid) {
          this.currentCustomerUid = customer.uid;
          this.loadExistingKpis();
          this.loadVehiclesForCustomer(customer.uid);
        }
      });
  }

  private loadVehiclesForCustomer(customerUid: string): void {
    this.filterRefService.getVehiclesOfCustomers_20AllFilter$(customerUid)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (vehicles: listAssets[]) => {
          this.availableVehicles = vehicles;
          this.filteredAvailableVehicles = [...vehicles];
          this.logger.debug('kpi-configuration', `Caricati ${vehicles.length} veicoli per customer ${customerUid}`);
        },
        error: (err) => {
          this.logger.error('kpi-configuration', 'Errore caricamento veicoli', err);
        }
      });
  }

  /**
   * Imposta la locale del datepicker in base alla lingua corrente
   */
  private setDatepickerLocale(): void {
    const currentLang = this.translateService.currentLang || this.dataService.getLanguage() || 'en';
    // Mappa le lingue al formato locale
    const localeMap: { [key: string]: string } = {
      'it': 'it-IT',
      'en': 'en-GB',
      'fr': 'fr-FR',
      'de': 'de-DE',
      'es': 'es-ES',
      'pt': 'pt-PT'
    };
    const locale = localeMap[currentLang] || 'en-GB';
    this.dateAdapter.setLocale(locale);
    this.logger.debug('kpi-configuration', `Datepicker locale impostato su: ${locale}`);
  }

  /**
   * Carica solo gli operatori KPI (i segnali vengono passati dal parent component)
   */
  private loadKpiOperators(): void {
    this.isLoading = true;

    this.kpiCrudService.getAllKpiOperators()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.logger.debug('kpi-configuration', 'Risposta API operatori:', response);

          if (response.result === 'OK' && response.data) {
            this.kpiOperators = response.data;
            this.logger.debug('kpi-configuration', `Caricati ${this.kpiOperators.length} operatori:`, this.kpiOperators);
          } else {
            this.logger.warn('kpi-configuration', 'Nessun operatore ricevuto o risposta non OK', response);
          }

          this.isLoading = false;
        },
        error: (error) => {
          this.logger.error('kpi-configuration', 'Errore caricamento operatori', error);
          this.formError = this.translateService.instant('kpiConfiguration.loadError');
          this.isLoading = false;
        }
      });
  }

  /**
   * Carica i KPI esistenti per il customer corrente
   * Filtra localmente scope='kpi_custom' dal catalogo (già caricato/cachato da CatalogService)
   */
  reloadKpis(): void {
    this.loadExistingKpis();
  }

  loadExistingKpis(): void {
    if (!this.currentCustomerUid) return;

    this.catalogService.getCatalogForCustomer(this.currentCustomerUid)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (catalog) => {
          this.fullCatalogData = catalog.data ?? [];
          const items = catalog.data as unknown as CommonAttribute[];
          this.existingKpis = items.filter(kpi => kpi.scope === 'kpi_custom');
          this.filterAndGroupKpis();
          this.applySorting();
          this.logger.debug('kpi-configuration', `Caricati ${this.existingKpis.length} KPI custom dal catalogo`);
        },
        error: (error) => {
          this.logger.error('kpi-configuration', 'Errore caricamento dati dal catalogo', error);
        }
      });
  }

  /**
   * Filtra e raggruppa i KPI per scope
   */
  filterAndGroupKpis(): void {
    // Filtra per termine di ricerca
    if (this.searchTerm.trim()) {
      const term = this.searchTerm.toLowerCase();
      this.filteredKpis = this.existingKpis.filter(kpi => {
        const label = this.getKpiLabel(kpi).toLowerCase();
        const name = (kpi.name || '').toLowerCase();
        const func = (kpi.function || '').toLowerCase();
        return label.includes(term) || name.includes(term) || func.includes(term);
      });
    } else {
      this.filteredKpis = [...this.existingKpis];
    }

    // Raggruppa per scope
    this.kpisByScope = {};
    this.filteredKpis.forEach(kpi => {
      const scope = kpi.scope || 'other';
      if (!this.kpisByScope[scope]) {
        this.kpisByScope[scope] = [];
      }
      this.kpisByScope[scope].push(kpi);
    });

    // Estrai gli scope disponibili
    this.kpiScopes = Object.keys(this.kpisByScope).sort();
  }

  /**
   * Handler per cambio termine di ricerca
   */
  onSearchChange(): void {
    this.currentPage = 1; // Reset alla prima pagina
    this.filterAndGroupKpis();
    this.applySorting(); // Applica ordinamento corrente
  }

  /**
   * Conta KPI per scope
   */
  getKpiCountForScope(scope: string): number {
    return this.kpisByScope[scope]?.length || 0;
  }

  // ==================== PAGINAZIONE ====================

  /**
   * Calcola il numero totale di pagine
   */
  get totalPages(): number {
    return Math.ceil(this.filteredKpis.length / this.pageSize);
  }

  /**
   * Ritorna i KPI paginati
   */
  get paginatedKpis(): CommonAttribute[] {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    return this.filteredKpis.slice(start, end);
  }

  /**
   * Handler per cambio pagina dal p-paginator (PrimeNG)
   */
  onPrimePageChange(event: any): void {
    this.currentPage = (event.first / event.rows) + 1;
    this.pageSize = event.rows;
  }

  // ==================== ORDINAMENTO ====================

  /**
   * Cambia il campo di ordinamento
   */
  toggleSort(field: 'label' | 'name' | 'function'): void {
    if (this.sortField === field) {
      // Stesso campo: inverti direzione
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      // Campo diverso: imposta nuovo campo e direzione asc
      this.sortField = field;
      this.sortDirection = 'asc';
    }
    this.applySorting();
  }

  /**
   * Applica l'ordinamento alla lista filtrata
   */
  private applySorting(): void {
    this.filteredKpis.sort((a, b) => {
      let valueA: string;
      let valueB: string;

      switch (this.sortField) {
        case 'label':
          valueA = this.getKpiLabel(a).toLowerCase();
          valueB = this.getKpiLabel(b).toLowerCase();
          break;
        case 'name':
          valueA = (a.name || '').toLowerCase();
          valueB = (b.name || '').toLowerCase();
          break;
        case 'function':
          valueA = (a.function || '').toLowerCase();
          valueB = (b.function || '').toLowerCase();
          break;
        default:
          valueA = '';
          valueB = '';
      }

      const comparison = valueA.localeCompare(valueB);
      return this.sortDirection === 'asc' ? comparison : -comparison;
    });
    this.currentPage = 1; // Reset alla prima pagina
  }

  /**
   * Ritorna l'icona corrente per il campo di ordinamento
   */
  getSortIcon(field: 'label' | 'name' | 'function'): any {
    if (this.sortField !== field) {
      return this.faSort;
    }
    return this.sortDirection === 'asc' ? this.faSortUp : this.faSortDown;
  }

  /**
   * Verifica se il campo è ordinato
   */
  isSortedBy(field: 'label' | 'name' | 'function'): boolean {
    return this.sortField === field;
  }

  // ==================== INLINE EDITING ====================

  /**
   * Avvia l'editing inline di un KPI
   */
  startInlineEdit(kpi: CommonAttribute): void {
    this.inlineEditingKpiUid = kpi.uid || null;
    this.inlineEditOperator = this.kpiOperators.find(o => o.function === kpi.function) || null;

    // Inizializza il campo multilingua con placeholder dal label del segnale
    const signal = this.getSignalFromKpi(kpi);
    this.inlineEditNameField = new MultiLangField('name');
    this.inlineEditNameField.defaultPlaceholder = signal?.label || signal?.name || '';
    setMultiLanguageField(this.inlineEditNameField);
    if (kpi.i18nLabel) {
      Object.entries(kpi.i18nLabel).forEach(([langKey, value]) => {
        const lang = langKey.split('-')[0];
        const labelItem = this.inlineEditNameField!.labels.find(l => l.lang === lang);
        if (labelItem) {
          labelItem.value = value;
        }
      });
    }
  }

  /**
   * Annulla l'editing inline
   */
  cancelInlineEdit(): void {
    this.inlineEditingKpiUid = null;
    this.inlineEditOperator = null;
    this.inlineEditNameField = null;
    this.inlineEditSaving = false;
  }

  /**
   * Get signal from KPI
   */
  getSignalFromKpi(kpi: CommonAttribute): CommonAttribute | null {
    return this.signals.find(s => s.name === kpi.name) || null;
  }

  /**
   * Etichetta (tradotta) del segnale sorgente del KPI, mostrata nel riepilogo accanto al nome tecnico.
   * Ritorna null se coincide con il nome tecnico, per non ripetere la stessa informazione.
   */
  getSourceSignalLabel(kpi: CommonAttribute): string | null {
    const signal = this.getSignalFromKpi(kpi);
    if (!signal) return null;
    const label = this.getKpiLabel(signal);
    return label && label !== kpi.name ? label : null;
  }

  /**
   * Verifica se un KPI è in modalità editing inline
   */
  isInlineEditing(kpi: CommonAttribute): boolean {
    return this.inlineEditingKpiUid === kpi.uid;
  }

  /**
   * Validazione form inline
   */
  isInlineFormValid(): boolean {
    if (!this.inlineEditOperator || !this.inlineEditNameField) return false;
    return this.inlineEditNameField.labels.some(l => l.value && l.value.trim() !== '');
  }

  /**
   * Handler per cambio operatore inline
   */
  onInlineOperatorChange(operator: KpiOperator): void {
    this.inlineEditOperator = operator;
  }

  /**
   * Handler per cambio nome inline
   */
  onInlineNameChange(field: MultiLangField): void {
    this.inlineEditNameField = field;
  }

  /**
   * Validazione per test inline (richiede operatore selezionato)
   */
  isInlineTestValid(): boolean {
    return !!this.inlineEditOperator;
  }

  /**
   * Apre il form di test per qualsiasi KPI (anche ereditati), direttamente dalla vista lista
   */
  testKpiDirect(kpi: CommonAttribute): void {
    if (!this.currentCustomerUid || !kpi.name || !kpi.function) return;

    this.selectedSignal = {
      name: kpi.name,
      label: kpi.label,
      type: kpi.type,
      unit: kpi.unit
    } as CommonAttribute;
    this.selectedOperator = this.kpiOperators.find(o => o.function === kpi.function) || null;

    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);

    this.testStartDate = thirtyDaysAgo;
    this.testEndDate = today;
    this.testSelectedVehicles = [];
    this.testResult = null;
    this.showTestForm = true;
  }

  /**
   * Apre il form per testare il KPI in modalità inline edit
   */
  testInlineKpi(kpi: CommonAttribute): void {
    if (!this.isInlineTestValid()) {
      this.formError = this.translateService.instant('kpiConfiguration.testFormIncomplete');
      return;
    }

    if (!this.currentCustomerUid) {
      this.formError = this.translateService.instant('kpiConfiguration.noCustomerError');
      return;
    }

    if (!kpi.name) {
      this.formError = this.translateService.instant('kpiConfiguration.noSignalSelected');
      return;
    }

    // Imposta temporaneamente selectedSignal e selectedOperator per il test
    // (il modal usa queste variabili)
    this.selectedSignal = {
      name: kpi.name,
      label: kpi.label,
      type: kpi.type,
      unit: kpi.unit
    } as CommonAttribute;
    this.selectedOperator = this.inlineEditOperator;

    // Imposta date di default (ultimi 30 giorni)
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);

    this.testStartDate = thirtyDaysAgo;
    this.testEndDate = today;
    this.testSelectedVehicles = [];
    this.testResult = null;
    this.showTestForm = true;
  }

  /**
   * Salva le modifiche inline
   */
  saveInlineEdit(kpi: CommonAttribute): void {
    if (!this.isInlineFormValid() || !kpi.uid) return;

    const duplicateError = this.findDuplicateKpiError(
      kpi.name,
      this.inlineEditOperator!.function,
      this.inlineEditNameField!.labels.map(l => l.value),
      kpi.uid
    );
    if (duplicateError) {
      this.formError = duplicateError;
      setTimeout(() => this.formError = null, 5000);
      return;
    }

    this.inlineEditSaving = true;

    // Costruisce i18nLabel
    const i18nLabel: { [key: string]: string } = {};
    this.inlineEditNameField!.labels.forEach(label => {
      if (label.value && label.value.trim() !== '') {
        const fullLangKey = this.getFullLanguageKey(label.lang);
        i18nLabel[fullLangKey] = label.value;
      }
    });

    // Il BE gestisce l'associazione tramite customerUids: passiamo sempre l'array
    // letto dal catalogo, con fallback al customer della tab corrente.
    const customerUids = kpi.customerUids?.length
      ? kpi.customerUids
      : (this.currentCustomerUid ? [this.currentCustomerUid] : []);

    const request: CommonAttributeRequest = {
      name: kpi.name,
      label: kpi.label,
      type: kpi.type || 'double',
      scope: kpi.scope || 'kpi_custom',
      function: this.inlineEditOperator!.function,
      unit: kpi.unit,
      customerUids: customerUids,
      i18nLabel: i18nLabel
    };

    this.formError = null;

    this.kpiCrudService.updateCommonAttribute(kpi.uid, request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.result !== 'OK') {
            const errorMsg = response.errorDescription || this.translateService.instant('kpiConfiguration.saveError');
            this.formError = errorMsg;
            this.logger.error('kpi-configuration', 'Errore update inline', response.errorDescription);
            setTimeout(() => this.formError = null, 5000);
            this.inlineEditSaving = false;
            return;
          }

          this.logger.info('kpi-configuration', `KPI ${kpi.uid} aggiornato inline`);

          // Aggiorna il catalogo locale con i dati modificati, prima di ri-salvarlo
          const catalogItem = this.fullCatalogData.find((item: any) => item.uid === kpi.uid);
          if (catalogItem) {
            catalogItem.function = request.function;
            catalogItem.i18nLabel = request.i18nLabel;
          }

          if (!this.currentCustomerUid) {
            this.loadExistingKpis();
            this.cancelInlineEdit();
            this.inlineEditSaving = false;
            return;
          }

          this.catalogService.invalidateCache(this.currentCustomerUid);
          this.catalogService.saveCatalogConfiguration(this.fullCatalogData, this.currentCustomerUid, this.selectedAudience)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.loadExistingKpis();
                this.cancelInlineEdit();
                this.configurationSaved.emit();
                this.inlineEditSaving = false;
              },
              error: (error) => {
                this.formError = this.translateService.instant('kpiConfiguration.saveError');
                this.logger.error('kpi-configuration', 'Errore salvataggio catalogo dopo update inline', error);
                setTimeout(() => this.formError = null, 5000);
                this.inlineEditSaving = false;
              }
            });
        },
        error: (error) => {
          const errorMessage = error?.error?.message || error?.message || this.translateService.instant('kpiConfiguration.saveError');
          this.formError = errorMessage;
          this.logger.error('kpi-configuration', 'Errore salvataggio inline', error);
          setTimeout(() => this.formError = null, 5000);
          this.inlineEditSaving = false;
        }
      });
  }

  /**
   * Toggle espansione form
   */
  toggleForm(): void {
    this.isFormExpanded = !this.isFormExpanded;
    if (!this.isFormExpanded) {
      this.resetForm();
    }
  }

  /**
   * Reset del form
   */
  resetForm(): void {
    this.selectedSignal = null;
    this.selectedOperator = null;
    this.kpiNameField = new MultiLangField('kpiName');
    this.kpiNameField.defaultPlaceholder = '';
    setMultiLanguageField(this.kpiNameField);
    this.formError = null;
    this.testResult = null;
    this.isEditing = false;
    this.editingKpiUid = null;
  }

  /**
   * Apre il form per modifica
   */
  editKpi(kpi: CommonAttribute): void {
    this.isEditing = true;
    this.editingKpiUid = kpi.uid || null;
    this.isFormExpanded = true;

    // Popola i campi
    this.selectedSignal = this.signals.find(s => s.name === kpi.name) || null;
    this.selectedOperator = this.kpiOperators.find(o => o.function === kpi.function) || null;

    // Popola le label multilingua
    this.kpiNameField = new MultiLangField('kpiName');
    this.kpiNameField.defaultPlaceholder = this.selectedSignal?.label || '';
    setMultiLanguageField(this.kpiNameField);
    if (kpi.i18nLabel) {
      Object.entries(kpi.i18nLabel).forEach(([langKey, value]) => {
        const lang = langKey.split('-')[0]; // 'it-IT' -> 'it'
        const labelItem = this.kpiNameField.labels.find(l => l.lang === lang);
        if (labelItem) {
          labelItem.value = value;
        }
      });
    }
  }

  /**
   * Elimina un KPI
   */
  deleteKpi(kpi: CommonAttribute): void {
    if (!kpi.uid) return;

    const kpiLabel = this.getKpiLabel(kpi);
    const confirmMessage = this.translateService.instant('kpiConfiguration.deleteConfirmMessage', { name: kpiLabel });

    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      width: '500px',
      data: confirmMessage,
      position: { top: '40px' },
      restoreFocus: true,
      disableClose: false
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && kpi.uid) {
        // Utente ha confermato - procedi con l'eliminazione.
        // Il BE gestisce l'associazione tramite customerUids: passiamo sempre
        // l'array letto dal catalogo, con fallback al customer della tab corrente.
        const customerUids = kpi.customerUids?.length
          ? kpi.customerUids
          : (this.currentCustomerUid ? [this.currentCustomerUid] : []);
        this.kpiCrudService.deleteCommonAttribute(kpi.uid, customerUids)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              if (response.result === 'OK') {
                this.existingKpis = this.existingKpis.filter(k => k.uid !== kpi.uid);
                this.filterAndGroupKpis();
                this.fullCatalogData = this.fullCatalogData.filter(i => i.uid !== kpi.uid);
                if (this.currentCustomerUid) {
                  this.catalogService.invalidateCache(this.currentCustomerUid);
                  this.catalogService.saveCatalogConfiguration(this.fullCatalogData, this.currentCustomerUid, this.selectedAudience)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe(() => this.configurationSaved.emit());
                }
                this.logger.info('kpi-configuration', `KPI ${kpi.uid} eliminato con successo`);

                // Mostra messaggio di successo
                this.testResult = {
                  success: true,
                  message: this.translateService.instant('kpiConfiguration.deleteSuccess', { name: kpiLabel }),
                  data: undefined
                };
                setTimeout(() => this.testResult = null, 3000);
              } else {
                // Errore dalla risposta API
                const errorMsg = response.errorDescription || this.translateService.instant('kpiConfiguration.deleteError');
                this.formError = errorMsg;
                this.logger.error('kpi-configuration', 'Errore eliminazione KPI', errorMsg);
                setTimeout(() => this.formError = null, 5000);
              }
            },
            error: (error) => {
              // Errore di rete o altro
              const errorMessage = error?.message || error?.error?.message || this.translateService.instant('kpiConfiguration.deleteError');
              this.formError = errorMessage;
              this.logger.error('kpi-configuration', 'Errore eliminazione KPI', error);
              setTimeout(() => this.formError = null, 5000);
            }
          });
      }
      // Se result è false/null, l'utente ha annullato - non fare nulla
    });
  }

  /**
   * Controllo duplicati tra i KPI custom esistenti (escluso quello in modifica).
   * Ritorna il messaggio d'errore tradotto se esiste già un KPI con:
   * - stesso segnale sorgente e stesso operatore, oppure
   * - stesso nome (confronto case-insensitive su tutte le etichette multilingua).
   * Ritorna null se non ci sono conflitti.
   */
  private findDuplicateKpiError(
    signalName: string | undefined,
    operatorFunction: string | undefined,
    newLabels: (string | null | undefined)[],
    excludeUid: string | null | undefined
  ): string | null {
    const norm = (v: string | null | undefined) => (v || '').trim().toLowerCase();
    const signal = norm(signalName);
    const fn = norm(operatorFunction);
    const names = new Set(newLabels.map(norm).filter(v => v !== ''));

    for (const existing of this.existingKpis) {
      if (excludeUid && existing.uid === excludeUid) continue;

      if (signal && fn && norm(existing.name) === signal && norm(existing.function) === fn) {
        return this.translateService.instant('kpiConfiguration.duplicateSignalOperator');
      }

      const existingNames = [existing.label, ...Object.values(existing.i18nLabel ?? {})].map(norm);
      if (existingNames.some(n => n !== '' && names.has(n))) {
        return this.translateService.instant('kpiConfiguration.duplicateName');
      }
    }
    return null;
  }

  /**
   * Validazione form completo (per salvataggio)
   */
  isFormValid(): boolean {
    const hasName = this.kpiNameField.labels.some(l => l.value && l.value.trim() !== '');
    const hasSignal = !!this.selectedSignal;
    const hasOperator = !!this.selectedOperator;
    return hasSignal && hasOperator && hasName;
  }

  /**
   * Validazione per test (non richiede nome KPI)
   */
  isTestValid(): boolean {
    const hasSignal = !!this.selectedSignal;
    const hasOperator = !!this.selectedOperator;
    return hasSignal && hasOperator;
  }

  /**
   * Apre il form per testare il KPI
   */
  testKpi(): void {
    if (!this.isTestValid()) {
      this.formError = this.translateService.instant('kpiConfiguration.testFormIncomplete');
      return;
    }

    if (!this.currentCustomerUid) {
      this.formError = this.translateService.instant('kpiConfiguration.noCustomerError');
      return;
    }

    if (!this.selectedSignal || !this.selectedSignal.name) {
      this.formError = this.translateService.instant('kpiConfiguration.noSignalSelected');
      return;
    }

    // Imposta date di default (ultimi 30 giorni)
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);

    this.testStartDate = thirtyDaysAgo;
    this.testEndDate = today;
    this.testSelectedVehicles = [];
    this.testResult = null;
    this.showTestForm = true;
  }

  /**
   * Esegue il test del KPI con i parametri selezionati
   */
  executeTest(): void {
    if (!this.testStartDate || !this.testEndDate) {
      this.formError = this.translateService.instant('kpiConfiguration.testMissingDates');
      return;
    }

    if (!this.currentCustomerUid || !this.selectedSignal?.name) {
      return;
    }

    this.isTesting = true;
    this.testResult = null;
    const selectedFunction = this.selectedOperator?.function || '';
    const signalName = this.selectedSignal.name;

    // Formatta le date in ISO string
    const startDateISO = this.testStartDate.toISOString();
    const endDateISO = this.testEndDate.toISOString();

    // Parametri per il test
    const params = [
      this.currentCustomerUid,                  // customerUid dallo store
      [signalName],                             // array di segnali
      startDateISO,                             // dataInizio
      endDateISO,                               // dataFine
      "Europe/Rome",                            // timezone
      this.testSeparateData,                    // includeMeta / separa dati
      this.testSelectedVehicles                 // array di uidVehicle selezionati
    ];

    this.logger.debug('kpi-configuration', 'Esecuzione test KPI con parametri:', params);

    // Chiama l'endpoint functions/execute
    this.kpiCrudService.executeFunction(selectedFunction, params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.isTesting = false;

          // Estrae i valori dal risultato
          let resultMessage = '';
          const responseData = response?.data || [];
          // isAggregated dipende da testSeparateData (true = aggregato, false = separato per veicolo)
          let isAggregated = this.testSeparateData;
          let values: Array<{vin: string; value: any}> = [];

          if (responseData.length === 0) {
            resultMessage = this.translateService.instant('kpiConfiguration.testSuccess') +
                          ` (${this.selectedSignal?.name} ${this.selectedOperator?.operator}) - ` +
                          this.translateService.instant('kpiConfiguration.noDataAvailable');
          } else if (responseData.length === 1) {
            // Dati aggregati (AGGREGATE) o singolo veicolo
            const value = responseData[0].value !== undefined ? responseData[0].value : 'N/A';
            const vin = responseData[0].vin || 'N/A';
            if (vin === 'AGGREGATE') {
              isAggregated = true;
              resultMessage = this.translateService.instant('kpiConfiguration.testSuccess') +
                            ` (${this.selectedSignal?.name} ${this.selectedOperator?.operator})`;
              values = [{ vin: 'AGGREGATE', value }];
            } else {
              resultMessage = this.translateService.instant('kpiConfiguration.testSuccess') +
                            ` (${this.selectedSignal?.name} ${this.selectedOperator?.operator})`;
              values = [{ vin, value }];
            }
          } else {
            // Dati separati per VIN - crea array di valori
            isAggregated = false;
            resultMessage = this.translateService.instant('kpiConfiguration.testSuccess') +
                          ` (${this.selectedSignal?.name} ${this.selectedOperator?.operator}) - ` +
                          this.translateService.instant('kpiConfiguration.vehiclesFoundMessage', { count: responseData.length });
            values = responseData.map((item: any) => ({
              vin: item.vin || 'N/A',
              value: item.value !== undefined ? item.value : 'N/A'
            }));
          }

          this.testResult = {
            success: true,
            message: resultMessage,
            data: responseData,
            isAggregated,
            values
          };
          this.logger.info('kpi-configuration', 'Test KPI eseguito con successo', response);
        },
        error: (error) => {
          this.isTesting = false;
          this.testResult = {
            success: false,
            message: this.translateService.instant('kpiConfiguration.testError') +
                     `: ${error?.error?.message || error.message || this.translateService.instant('global.unknownError')}`
          };
          this.logger.error('kpi-configuration', 'Errore durante il test KPI', error);
        }
      });
  }

  /**
   * Chiude il form di test
   */
  closeTestForm(): void {
    this.showTestForm = false;
    this.testStartDate = null;
    this.testEndDate = null;
    this.testSelectedVehicles = [];
    this.testSeparateData = false;
  }

  /**
   * Salva il KPI
   */
  saveKpi(): void {
    if (!this.isFormValid()) {
      this.formError = this.translateService.instant('kpiConfiguration.formIncomplete');
      return;
    }

    if (!this.currentCustomerUid) {
      this.formError = this.translateService.instant('kpiConfiguration.noCustomerError');
      return;
    }

    const duplicateError = this.findDuplicateKpiError(
      this.selectedSignal!.name,
      this.selectedOperator!.function,
      this.kpiNameField.labels.map(l => l.value),
      this.isEditing ? this.editingKpiUid : null
    );
    if (duplicateError) {
      this.formError = duplicateError;
      return;
    }

    this.isSaving = true;
    this.formError = null;

    // Il BE gestisce l'associazione tramite customerUids (sempre come array).
    // In update preserviamo l'array letto dal catalogo; in create usiamo il
    // customer della tab corrente. Fallback comune al customer corrente.
    const editingKpi = this.isEditing && this.editingKpiUid
      ? this.existingKpis.find(k => k.uid === this.editingKpiUid)
      : null;
    const customerUids = editingKpi?.customerUids?.length
      ? editingKpi.customerUids
      : (this.currentCustomerUid ? [this.currentCustomerUid] : []);

    const request: CommonAttributeRequest = {
      name: this.selectedSignal!.name,
      label: this.selectedSignal!.label,
      type: this.selectedSignal!.type || 'double',
      scope: 'kpi_custom',
      function: this.selectedOperator!.function,
      unit: this.selectedSignal!.unit,
      customerUids: customerUids,
      i18nLabel: this.buildI18nLabel()
    };

    const operation$ = this.isEditing && this.editingKpiUid
      ? this.kpiCrudService.updateCommonAttribute(this.editingKpiUid, request)
      : this.kpiCrudService.createCommonAttribute(request);

    operation$.pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.result === 'OK') {
            this.logger.info('kpi-configuration', `KPI ${this.isEditing ? 'aggiornato' : 'creato'} con successo`);
            if (this.currentCustomerUid) {
              this.catalogService.invalidateCache(this.currentCustomerUid);
            }
            this.loadExistingKpis();
            this.resetForm();
            this.isFormExpanded = false;
            this.configurationSaved.emit();
          } else {
            this.formError = response.errorDescription || this.translateService.instant('kpiConfiguration.saveError');
          }
          this.isSaving = false;
        },
        error: (error) => {
          this.logger.error('kpi-configuration', 'Errore salvataggio KPI', error);
          this.formError = this.translateService.instant('kpiConfiguration.saveError');
          this.isSaving = false;
        }
      });
  }

  /**
   * Costruisce l'oggetto i18nLabel dalle labels multilingua
   */
  private buildI18nLabel(): { [key: string]: string } {
    const i18nLabel: { [key: string]: string } = {};
    this.kpiNameField.labels.forEach(label => {
      if (label.value && label.value.trim() !== '') {
        // Converti 'it' -> 'it-IT', 'en' -> 'en-US', etc.
        const fullLangKey = this.getFullLanguageKey(label.lang);
        i18nLabel[fullLangKey] = label.value;
      }
    });
    return i18nLabel;
  }

  /**
   * Converte codice lingua breve in formato completo
   */
  private getFullLanguageKey(lang: string): string {
    const langMap: { [key: string]: string } = {
      'it': 'it-IT',
      'en': 'en-US',
      'fr': 'fr-FR',
      'de': 'de-DE',
      'es': 'es-ES',
      'pt': 'pt-PT'
    };
    return langMap[lang] || `${lang}-${lang.toUpperCase()}`;
  }

  /**
   * Ottiene la label tradotta di un KPI
   */
  isKpiReadOnly(kpi: CommonAttribute): boolean {
    if (!kpi.customerUid) return false;
    return kpi.customerUid !== this.currentCustomerUid;
  }

  getParentCustomerName(kpi: CommonAttribute): string {
    if (!kpi.customerUid) return '';
    // Store, altrimenti backend (cliente padre non presente nello store per un utente figlio)
    return this.customerNameMap.get(kpi.customerUid) || this.customerNames.resolve(kpi.customerUid) || kpi.customerUid;
  }

  isKpiDeleted(kpi: CommonAttribute): boolean {
    if (!kpi.name) return false;
    return !this.fullCatalogData.some((item: any) => item.name === kpi.name && item.scope !== 'kpi_custom');
  }

  isKpiDisabled(kpi: CommonAttribute): boolean {
    if (!kpi.name) return false;
    const signal = this.fullCatalogData.find((item: any) => item.name === kpi.name && item.scope !== 'kpi_custom');
    return signal ? signal.visible === false : false;
  }

  getKpiLabel(kpi: CommonAttribute): string {
    const currentLang = localStorage.getItem('language') || 'en';
    const fullLangKey = this.getFullLanguageKey(currentLang);

    if (kpi.i18nLabel && kpi.i18nLabel[fullLangKey]) {
      return kpi.i18nLabel[fullLangKey];
    }
    return kpi.label || kpi.name || '';
  }

  /**
   * Ottiene la label nella lingua corrente dell'utente per visualizzazione
   */
  getCurrentLanguageLabel(kpi: CommonAttribute): string | null {
    if (!kpi.i18nLabel) return null;

    const currentLang = localStorage.getItem('language') || 'en';
    const fullLangKey = this.getFullLanguageKey(currentLang);

    if (kpi.i18nLabel[fullLangKey]) {
      return kpi.i18nLabel[fullLangKey];
    }
    return null;
  }

  /**
   * Ottiene il codice lingua corrente formattato
   */
  getCurrentLanguageCode(): string {
    const currentLang = localStorage.getItem('language') || 'en';
    return this.getFullLanguageKey(currentLang);
  }

  /**
   * Ottiene l'operator symbol dato il function name
   */
  getOperatorSymbol(functionName: string): string {
    const operator = this.kpiOperators.find(op => op.function === functionName);
    return operator?.operator || functionName;
  }

  /**
   * Handler per il cambio di segnale selezionato
   */
  onSignalChange(): void {
    // Aggiorna il placeholder del nome KPI con il label del segnale selezionato
    if (this.selectedSignal) {
      this.kpiNameField.defaultPlaceholder = this.selectedSignal.label || this.selectedSignal.name || '';
    } else {
      this.kpiNameField.defaultPlaceholder = '';
    }
    // Pulisci eventuali errori e risultati di test precedenti
    this.formError = null;
    this.testResult = null;
  }

  /**
   * Handler per cambio campo multilingua
   */
  onKpiNameChange(field: MultiLangField): void {
    this.kpiNameField = field;
  }

  /**
   * Verifica se un KPI ha i18nLabel definiti
   */
  hasI18nLabels(kpi: CommonAttribute): boolean {
    return !!(kpi.i18nLabel && Object.keys(kpi.i18nLabel).length > 0);
  }

  /**
   * Ottiene le chiavi lingua di un KPI
   */
  getI18nLanguages(kpi: CommonAttribute): string[] {
    if (!kpi.i18nLabel) return [];
    return Object.keys(kpi.i18nLabel);
  }

  /**
   * Toggle all vehicles selection
   */
  toggleAllVehicles(checked: boolean): void {
    if (checked) {
      // Select all vehicles (considering filter)
      const vehiclesToSelect = this.vehicleFilterText
        ? this.filteredAvailableVehicles.map(v => v.uid)
        : this.availableVehicles.map(v => v.uid);
      this.testSelectedVehicles = vehiclesToSelect;
    } else {
      // Deselect all
      this.testSelectedVehicles = [];
    }
  }

  /**
   * Select all vehicles (max 25)
   */
  selectAllVehicles(): void {
    const vehiclesToSelect = this.vehicleFilterText
      ? this.filteredAvailableVehicles.map(v => v.uid)
      : this.availableVehicles.map(v => v.uid);
    this.testSelectedVehicles = vehiclesToSelect.slice(0, this.MAX_VEHICLES);
  }

  /**
   * Deselect all vehicles
   */
  deselectAllVehicles(): void {
    this.testSelectedVehicles = [];
  }

  /**
   * Check if maximum vehicles limit is reached
   */
  isMaxVehiclesReached(): boolean {
    return this.testSelectedVehicles && this.testSelectedVehicles.length >= this.MAX_VEHICLES;
  }

  /**
   * Handle vehicle filter change
   */
  onVehicleFilterChange(): void {
    if (!this.vehicleFilterText || this.vehicleFilterText.trim() === '') {
      this.filteredAvailableVehicles = [...this.availableVehicles];
    } else {
      const filterLower = this.vehicleFilterText.toLowerCase().trim();
      this.filteredAvailableVehicles = this.availableVehicles.filter(vehicle => {
        const vin = (vehicle.vin || '').toLowerCase();
        const plate = (vehicle.plate || '').toLowerCase();
        const internalId = (vehicle.internalId || '').toLowerCase();
        const brandName = (vehicle.vehicleModel?.vehicleBrand?.name || '').toLowerCase();
        const modelName = (vehicle.vehicleModel?.name || '').toLowerCase();

        return vin.includes(filterLower) ||
               plate.includes(filterLower) ||
               internalId.includes(filterLower) ||
               brandName.includes(filterLower) ||
               modelName.includes(filterLower);
      });
    }
  }

  /**
   * Get vehicle label by UID
   */
  getVehicleLabel(vehicleUid: string): string {
    const vehicle = this.availableVehicles.find(v => v.uid === vehicleUid);
    if (!vehicle) return vehicleUid;

    let label = vehicle.plate || vehicle.internalId;
    if (vehicle.vehicleModel?.name) {
      label += ` - ${vehicle.vehicleModel.vehicleBrand?.name || ''} ${vehicle.vehicleModel.name}`;
    }
    return label;
  }

  /**
   * Clear test result when parameters change
   */
  clearTestResult(): void {
    this.testResult = null;
  }

  /**
   * Toggle test result expanded state
   */
  toggleTestResultExpanded(): void {
    if (this.testResult?.success) {
      this.isTestResultExpanded = !this.isTestResultExpanded;
    }
  }
}
